const COMMIT_TYPE_ORDER = [
    "feature",
    "bugfix",
    "refactoring",
    "ci",
    "tests",
    "docs",
    "chore",
    "perf",
    "revert",
    "merge",
    "archive",
    "style",
    "other",
];
const commitTypeToId = new Map(COMMIT_TYPE_ORDER.map((c, i) => [c, i]));
const idToCommitType = (id) => id === undefined ? undefined : COMMIT_TYPE_ORDER[id];
export function classFromScore(score) {
    return score < 10 ? "XS" : score < 20 ? "S" : score < 40 ? "M" : score < 70 ? "L" : "XL";
}
export function serializeMonth(m) {
    return JSON.stringify(m);
}
export function deserializeMonth(raw) {
    const obj = JSON.parse(raw);
    // V4 format - current
    if (obj.v === 4)
        return obj;
    // V3 format - migrate to V4
    if (obj.v === 3) {
        const v3 = obj;
        const migrated = {
            v: 4,
            repos: [v3.repo],
            authors: v3.authors,
            commits: v3.commits.map(([hash, authorId, ts, score, f, ins, del, fn, cyclo, nest, typeId, msg]) => [
                hash,
                0, // repo index - since V3 had single repo, it's always index 0
                authorId,
                ts,
                score,
                f,
                ins,
                del,
                fn,
                cyclo,
                nest,
                typeId,
                msg,
            ]),
        };
        return migrated;
    }
    // V2 format - migrate through V3 to V4
    if (obj.v === 2) {
        const v2 = obj;
        const v3 = {
            v: 3,
            repo: v2.repo,
            authors: v2.authors,
            commits: v2.commits.map(([hash, authorId, ts, score, f, ins, del, fn, cyclo, nest, typeStr, msg]) => [
                hash,
                authorId,
                ts,
                score,
                f,
                ins,
                del,
                fn,
                cyclo,
                nest,
                typeStr ? commitTypeToId.get(typeStr) : undefined,
                msg,
            ]),
        };
        // Now migrate V3 to V4
        const migrated = {
            v: 4,
            repos: [v3.repo],
            authors: v3.authors,
            commits: v3.commits.map(([hash, authorId, ts, score, f, ins, del, fn, cyclo, nest, typeId, msg]) => [
                hash,
                0,
                authorId,
                ts,
                score,
                f,
                ins,
                del,
                fn,
                cyclo,
                nest,
                typeId,
                msg,
            ]),
        };
        return migrated;
    }
    // V1 format - migrate through V2, V3 to V4
    if (obj.v === 1) {
        const v1 = obj;
        const v3 = {
            v: 3,
            repo: v1.repo,
            authors: v1.authors,
            commits: v1.commits.map(([hash, authorId, isoTs, score, f, ins, del, fn, cyclo, nest, typeStr, msg]) => [
                hash,
                authorId,
                Math.floor(new Date(isoTs).getTime() / 1000),
                score,
                f,
                ins,
                del,
                fn,
                cyclo,
                nest,
                typeStr ? commitTypeToId.get(typeStr) : undefined,
                msg,
            ]),
        };
        // Now migrate V3 to V4
        const migrated = {
            v: 4,
            repos: [v3.repo],
            authors: v3.authors,
            commits: v3.commits.map(([hash, authorId, ts, score, f, ins, del, fn, cyclo, nest, typeId, msg]) => [
                hash,
                0,
                authorId,
                ts,
                score,
                f,
                ins,
                del,
                fn,
                cyclo,
                nest,
                typeId,
                msg,
            ]),
        };
        return migrated;
    }
    throw new Error("Unsupported version");
}
export function createEmptyMonth(repo) {
    return { v: 4, repos: repo ? [repo] : [], authors: {}, commits: [] };
}
export function mergeCommits(existing, newRows, overwrite) {
    const idx = new Map();
    existing.commits.forEach((t, i) => idx.set(t[0], i));
    const authorIdByHandle = new Map();
    for (const [id, handle] of Object.entries(existing.authors))
        authorIdByHandle.set(handle, id);
    let nextAuthorId = Object.keys(existing.authors).length;
    // Map repo names to indices
    const repoToIndex = new Map();
    existing.repos.forEach((repo, idx) => repoToIndex.set(repo, idx));
    function ensureAuthorId(handle) {
        const found = authorIdByHandle.get(handle);
        if (found)
            return found;
        const id = String(nextAuthorId++);
        existing.authors[id] = handle;
        authorIdByHandle.set(handle, id);
        return id;
    }
    function ensureRepoIndex(repo) {
        const found = repoToIndex.get(repo);
        if (found !== undefined)
            return found;
        const idx = existing.repos.length;
        existing.repos.push(repo);
        repoToIndex.set(repo, idx);
        return idx;
    }
    for (const r of newRows) {
        const h = r.hash;
        const authorId = ensureAuthorId(r.author);
        const repoIdx = ensureRepoIndex(r.repo || "");
        const tsSeconds = Math.floor(new Date(r.timestamp).getTime() / 1000);
        // commitType may have been a string earlier; map to id if needed
        let typeId;
        if (r.commitType) {
            typeId = commitTypeToId.get(r.commitType);
        }
        const tuple = [
            r.hash,
            repoIdx,
            authorId,
            tsSeconds,
            r.score,
            r.metrics.filesChanged,
            r.metrics.insertions,
            r.metrics.deletions,
            r.metrics.functionsChanged,
            r.metrics.cyclomaticDelta,
            r.metrics.maxNestingDelta,
            typeId,
            r.message,
        ];
        if (idx.has(h)) {
            if (overwrite) {
                existing.commits[idx.get(h)] = tuple;
            }
        }
        else {
            existing.commits.push(tuple);
            idx.set(h, existing.commits.length - 1);
        }
    }
    return existing;
}
export function storedCommitTupleToRow(tuple, authors, repos) {
    const [hash, repoIdx, authorId, tsSeconds, score, filesChanged, insertions, deletions, functionsChanged, cyclomaticDelta, maxNestingDelta, commitTypeId, message,] = tuple;
    const repo = repos[repoIdx];
    const iso = new Date(tsSeconds * 1000).toISOString();
    const commitUrl = repo ? `https://github.com/${repo}/commit/${hash}` : "";
    return {
        hash,
        author: authors[authorId],
        timestamp: iso,
        score,
        class: classFromScore(score),
        commitType: idToCommitType(commitTypeId) || undefined,
        message,
        commitUrl,
        repo,
    };
}
